import { parseISO } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { useEffect, useState } from 'react'

export interface HandshakeCountdownResult {
  timeLeft: string
  hours: number
  minutes: number
  seconds: number
  isExpired: boolean
}

export type HandshakeCountdownFormat = 'MM:SS' | 'korean'

export interface UseHandshakeCountdownOptions {
  format?: HandshakeCountdownFormat
  timezone: string
}

/**
 * Custom hook for handshake countdown timer with timezone support
 * Returns remaining time information until expiration
 *
 * @param expiredAt - ISO8601 UTC string or Date object for expiration time
 * @param options - Configuration options
 * @param options.format - Time display format: 'MM:SS' (e.g., '05:30') or 'korean' (e.g., '5분 30초')
 * @param options.timezone - IANA timezone string (e.g., 'America/New_York', 'Asia/Seoul')
 */
export const useHandshakeCountdown = (
  expiredAt: string | Date | undefined,
  options: UseHandshakeCountdownOptions
): HandshakeCountdownResult => {
  const { format = 'MM:SS', timezone } = options

  const [result, setResult] = useState<HandshakeCountdownResult>({
    timeLeft: format === 'MM:SS' ? '00:00' : '',
    hours: 0,
    minutes: 0,
    seconds: 0,
    isExpired: false,
  })

  useEffect(() => {
    // Don't run countdown if expiredAt is not provided
    if (!expiredAt) {
      setResult({
        timeLeft: format === 'MM:SS' ? '00:00' : '',
        hours: 0,
        minutes: 0,
        seconds: 0,
        isExpired: true,
      })
      return
    }

    const updateCountdown = () => {
      // Get current time in the specified timezone
      const nowInTz = utcToZonedTime(new Date(), timezone)

      // Parse expiredAt and convert to the specified timezone
      let expiredAtDateInTz: Date
      if (typeof expiredAt === 'string') {
        // Parse ISO8601 UTC string and convert to specified timezone
        const expiredAtDateUTC = parseISO(expiredAt)
        expiredAtDateInTz = utcToZonedTime(expiredAtDateUTC, timezone)
      } else {
        expiredAtDateInTz = utcToZonedTime(expiredAt, timezone)
      }

      const diffInMs = expiredAtDateInTz.getTime() - nowInTz.getTime()

      if (diffInMs <= 0) {
        setResult({
          timeLeft: format === 'MM:SS' ? '00:00' : '0초',
          hours: 0,
          minutes: 0,
          seconds: 0,
          isExpired: true,
        })
        return
      }

      // Calculate remaining time
      const totalSeconds = Math.floor(diffInMs / 1000)
      const hours = Math.floor(totalSeconds / (60 * 60))
      const minutes = Math.floor((totalSeconds % (60 * 60)) / 60)
      const seconds = totalSeconds % 60

      // Format time string based on format option
      let timeLeft = ''
      if (format === 'MM:SS') {
        // Format as MM:SS (always show minutes and seconds)
        const paddedMinutes = Math.max(0, hours * 60 + minutes)
          .toString()
          .padStart(2, '0')
        const paddedSeconds = Math.max(0, seconds).toString().padStart(2, '0')
        timeLeft = `${paddedMinutes}:${paddedSeconds}`
      } else {
        // Korean format: show most significant units
        if (hours > 0) {
          timeLeft = `${hours}시간 ${minutes}분`
        } else if (minutes > 0) {
          timeLeft = `${minutes}분`
        } else {
          timeLeft = `${seconds}초`
        }
      }

      setResult({
        timeLeft,
        hours,
        minutes,
        seconds,
        isExpired: false,
      })
    }

    updateCountdown()
    const interval = setInterval(updateCountdown, 1000)

    return () => clearInterval(interval)
  }, [expiredAt, format, timezone])

  return result
}
